import { notification } from 'antd';
import Upload, { RcFile } from 'antd/es/upload';
import { IMAGE_SIZE_CALCULATION } from 'configs/constants';
import { MAX_FILE_SIZE_MB } from 'configs';
import { NotificationProps } from './types';
// import { PDFDocument } from 'pdf-lib';
import imageCompression from 'browser-image-compression';

type TranslateFunction = (key: string, variables?: Record<string, unknown>) => string;

export type AllowedFileTypes = 'images' | 'images-and-pdf' | 'default' | 'bulk-upload';

// Compression threshold constant
const COMPRESSION_THRESHOLD_MB = 2;

const getAllowedTypes = (allowedFileTypes: AllowedFileTypes) => {
  switch (allowedFileTypes) {
    case 'images':
      return ['image/jpeg', 'image/png', 'image/jpg'];
    case 'images-and-pdf':
      return ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    default:
      return ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf', 'video/mp4'];
  }
};

const getErrorMessage = (allowedFileTypes: AllowedFileTypes, t: (key: string) => string) => {
  switch (allowedFileTypes) {
    case 'images':
      return t('upload.error.fileTypeWithoutPdf');
    case 'images-and-pdf':
      return t('upload.error.fileTypeWithPdf');
    default:
      return t('upload.error.fileType');
  }
};

/**
 * Compress PDF with light compression using pdf-lib
 * Maintains readability while reducing file size minimally
 */
/*
export const compressPDF = async (
  file: RcFile,
  _t: TranslateFunction,
  _compressionThresholdBytes: number,
): Promise<RcFile> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);

    // Use light compression settings for minimal size reduction
    const compressedPdfBytes = await pdfDoc.save({
      useObjectStreams: true,
      addDefaultPage: false,
      updateFieldAppearances: false,
    });

    // Convert Uint8Array to ArrayBuffer to ensure compatibility
    const pdfArrayBuffer = compressedPdfBytes.buffer.slice(
      compressedPdfBytes.byteOffset,
      compressedPdfBytes.byteOffset + compressedPdfBytes.byteLength,
    );

    const compressedFile = new File([pdfArrayBuffer], file.name, {
      type: 'application/pdf',
      lastModified: Date.now(),
    });

    const compressedSizeMB = (compressedFile.size / (1024 * 1024)).toFixed(2);
    const compressionRatio = ((compressedFile.size / file.size) * 100).toFixed(1);
    console.log(`[PDF] Before: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`[PDF] After: ${compressedSizeMB} MB (${compressionRatio}% of original)`);

    return compressedFile as RcFile;
  } catch (error) {
    console.error('PDF compression failed:', error);
    return file;
  }
};
*/

/**
 * Compress image using browser-image-compression library
 * Maintains good quality while reducing file size
 */
export const compressImage = async (file: RcFile, _t: TranslateFunction): Promise<RcFile> => {
  try {
    const options = {
      maxSizeMB: MAX_FILE_SIZE_MB, // Max size from constant
      maxWidthOrHeight: 1920, // Max width/height
      useWebWorker: true,
      fileType: file.type,
    };

    const compressedFile = await imageCompression(file, options);

    // Convert to RcFile
    const compressedRcFile = new File([compressedFile], file.name, {
      type: compressedFile.type,
      lastModified: Date.now(),
    }) as RcFile;

    const compressedSizeMB = (compressedRcFile.size / (1024 * 1024)).toFixed(2);
    const compressionRatio = ((compressedRcFile.size / file.size) * 100).toFixed(1);
    console.log(`[Image] Before: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`[Image] After: ${compressedSizeMB} MB (${compressionRatio}% of original)`);

    return compressedRcFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    return file;
  }
};

/**
 * Compress file (images and PDFs) with light compression
 * Only compresses files above threshold, uses minimal quality reduction
 */
export const compressFile = async (file: RcFile, t: TranslateFunction): Promise<RcFile> => {
  const compressionThresholdBytes = COMPRESSION_THRESHOLD_MB * 1024 * 1024;

  // If file is already under threshold, return original file
  if (file.size <= compressionThresholdBytes) {
    return file;
  }

  try {
    let compressedFile: RcFile;
    if (file.type.startsWith('image/')) {
      compressedFile = await compressImage(file, t);
    } else {
      return file;
    }
    /*
    else if (file.type === 'application/pdf') {
      compressedFile = await compressPDF(file, t, compressionThresholdBytes);
    }
    */

    return compressedFile;
  } catch (error) {
    console.error('File compression failed:', error);
    return file;
  }
};

export const validateUploadSize = (
  file: RcFile,
  t: TranslateFunction,
  showSuccessMessage = true,
  allowedFileTypes: AllowedFileTypes = 'images-and-pdf',
) => {
  const allowedTypes = getAllowedTypes(allowedFileTypes);

  if (!allowedTypes.includes(file.type)) {
    notification.error({
      message: getErrorMessage(allowedFileTypes, t),
      ...NotificationProps,
    });
    return Upload.LIST_IGNORE;
  }

  const isLt5M = IMAGE_SIZE_CALCULATION(file.size);

  if (!isLt5M) {
    notification.error({
      message: t('upload.error.fileSize', { maxSize: MAX_FILE_SIZE_MB }),
      ...NotificationProps,
    });
    return Upload.LIST_IGNORE;
  }
  if (showSuccessMessage) {
    notification.success({
      message: t('upload.success'),
      ...NotificationProps,
    });
  }

  return false;
};

export const validateImageDimensions = async (
  file: RcFile,
  t: TranslateFunction,
  requiredWidth: number,
  requiredHeight: number,
): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== requiredWidth || img.height !== requiredHeight) {
          notification.error({
            message: t('upload.error.imageDimensions', { width: requiredWidth, height: requiredHeight }),
            ...NotificationProps,
          });
          resolve(false);
          return;
        }
        resolve(true);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Resizes an image file to the specified dimensions
 * @param {File} file - The image file to resize
 * @param {TranslateFunction} t - Translation function
 * @param {number} targetWidth - Target width in pixels
 * @param {number} targetHeight - Target height in pixels
 * @param {number} quality - Image quality (0-1, default: 0.9)
 * @param {string} outputFormat - Output format ('image/jpeg' or 'image/png')
 * @returns {Promise<File>} - Promise that resolves to the resized image file
 */

export const resizeImage = (
  file: File,
  t: TranslateFunction,
  targetWidth = 1120,
  targetHeight = 150,
  quality = 0.9,
  outputFormat = 'image/jpeg',
) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      notification.error({
        message: t('upload.error.canvasContext'),
        ...NotificationProps,
      });
      reject(new Error(t('upload.error.canvasContext')));
      return;
    }

    img.onload = () => {
      // Set canvas dimensions to target size
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Convert canvas to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a new File object with the resized image
            const resizedFile = new File([blob], file.name, {
              type: outputFormat,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            notification.error({
              message: t('upload.error.resizeFailed'),
              ...NotificationProps,
            });
            reject(new Error(t('upload.error.resizeFailed')));
          }
        },
        outputFormat,
        quality,
      );
    };

    img.onerror = () => {
      notification.error({
        message: t('upload.error.imageLoadFailed'),
        ...NotificationProps,
      });
      reject(new Error(t('upload.error.imageLoadFailed')));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        img.src = result;
      } else {
        notification.error({
          message: t('upload.error.fileReadFailed'),
          ...NotificationProps,
        });
        reject(new Error(t('upload.error.fileReadFailed')));
      }
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Alternative function that maintains aspect ratio and crops to fit
 * @param {File} file - The image file to resize
 * @param {TranslateFunction} t - Translation function
 * @param {number} targetWidth - Target width in pixels
 * @param {number} targetHeight - Target height in pixels
 * @param {number} quality - Image quality (0-1, default: 0.9)
 * @returns {Promise<File>} - Promise that resolves to the resized image file
 */
export const resizeAndCropImage = (
  file: File,
  t: TranslateFunction,
  targetWidth = 1120,
  targetHeight = 150,
  quality = 0.9,
) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      notification.error({
        message: t('upload.error.canvasContext'),
        ...NotificationProps,
      });
      reject(new Error(t('upload.error.canvasContext')));
      return;
    }

    img.onload = () => {
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Calculate crop dimensions to maintain aspect ratio
      const imgAspectRatio = img.width / img.height;
      const targetAspectRatio = targetWidth / targetHeight;

      let sourceX = 0,
        sourceY = 0,
        sourceWidth = img.width,
        sourceHeight = img.height;

      if (imgAspectRatio > targetAspectRatio) {
        // Image is wider than target, crop width
        sourceWidth = img.height * targetAspectRatio;
        sourceX = (img.width - sourceWidth) / 2;
      } else {
        // Image is taller than target, crop height
        sourceHeight = img.width / targetAspectRatio;
        sourceY = (img.height - sourceHeight) / 2;
      }

      // Draw the cropped and scaled image
      ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          } else {
            notification.error({
              message: t('upload.error.resizeFailed'),
              ...NotificationProps,
            });
            reject(new Error(t('upload.error.resizeFailed')));
          }
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => {
      notification.error({
        message: t('upload.error.imageLoadFailed'),
        ...NotificationProps,
      });
      reject(new Error(t('upload.error.imageLoadFailed')));
    };

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        img.src = result;
      } else {
        notification.error({
          message: t('upload.error.fileReadFailed'),
          ...NotificationProps,
        });
        reject(new Error(t('upload.error.fileReadFailed')));
      }
    };
    reader.readAsDataURL(file);
  });
};

export const validateBulkUpload = (file: RcFile, t: TranslateFunction, showSuccessMessage = true) => {
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
    'application/vnd.ms-excel', // xls
    'text/csv', // csv
  ];

  if (!allowedTypes.includes(file.type)) {
    notification.error({
      message: t('upload.error.bulkUploadFileType'),
      ...NotificationProps,
    });
    return Upload.LIST_IGNORE;
  }

  const isLt5M = IMAGE_SIZE_CALCULATION(file.size);

  if (!isLt5M) {
    notification.error({
      message: t('upload.error.fileSize', { maxSize: MAX_FILE_SIZE_MB }),
      ...NotificationProps,
    });
    return Upload.LIST_IGNORE;
  }

  if (showSuccessMessage) {
    notification.success({
      message: t('upload.success'),
      ...NotificationProps,
    });
  }

  return false;
};
